import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCartItems,
  selectActiveCartItems,
  selectCartTotalStake,
  selectCartTotalBetsCount,
  selectCartTotalPotentialPayout,
  selectBalance,
  selectUnitToken,
  selectUser,
  removeFromCart,
  clearCart,
} from '@homeplay/app';
import { apiRegisterBets, apiDeleteCart, apiSaveCart } from '@homeplay/api';
import { LOTTO_CONSTANTS } from '../../constants';
import { hasExpiredItems } from '../../utils/cartHelpers';
import { formatBalance } from '../../utils/common';
import { getLottoErrorMessage } from '../../utils/error';
import { useAssistantActions } from '../../hooks/useLottoAssistantContext';
import Drawer from '../../../../../components/src/UI/Drawer';
import CartItem from '../CartItem';
import {
  mapCartItemsToWagers,
  extractBetErrors,
  correlateBedsWithCartItems,
  buildRegisteredBetsPayload,
} from './utils';
import infoIcon from '../../../../../../shared/assets/images/lotto-assistant/info.svg';
import CloseButton from '../../../../../components/src/UI/CloseButton';
import classes from './CartDrawer.module.scss';

const CartDrawer = ({ isOpen, onClose }) => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [declineErrors, setDeclineErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const activeCartItems = useSelector(selectActiveCartItems);
  const totalStake = useSelector(selectCartTotalStake);
  const totalBetsCount = useSelector(selectCartTotalBetsCount);
  const totalPotentialPayout = useSelector(selectCartTotalPotentialPayout);
  const totalBalance = useSelector(selectBalance);
  const unitToken = useSelector(selectUnitToken);
  const user = useSelector(selectUser);
  const isUserLogin = !!user;

  const { trackLottoEvent } = useAssistantActions();

  const exceedsBalance = totalStake > totalBalance;
  const canPlaceBets = unitToken && cartItems.length > 0 && !exceedsBalance;

  useEffect(() => {
    if (
      error
      && error.includes(LOTTO_CONSTANTS.EXPIRED_ERROR_MESSAGE)
      && !hasExpiredItems(cartItems)
    ) {
      setError(null);
    }
  }, [cartItems, error]);

  const handleRemoveItem = (id) => dispatch(removeFromCart(id));

  const handleClearAll = () => {
    if (unitToken) apiDeleteCart({ unitToken }).catch(() => {});
    dispatch(clearCart());
  };

  const handlePlaceBets = async () => {
    setError(null);
    setSuccess(false);
    setDeclineErrors({});

    if (!canPlaceBets) return;

    if (hasExpiredItems(cartItems)) {
      setError(LOTTO_CONSTANTS.EXPIRED_ERROR_MESSAGE);
      return;
    }

    setIsLoading(true);
    try {
      const wagers = mapCartItemsToWagers(activeCartItems);
      const result = await apiRegisterBets({ unitToken, wagers });

      const betError = extractBetErrors(result);
      if (betError) {
        setError(betError);
        return;
      }

      const addedBets = result?.added_bets ?? [];
      const { acceptedIds, declineErrors: newDeclineErrors } = correlateBedsWithCartItems(
        addedBets,
        activeCartItems
      );

      if (Object.keys(newDeclineErrors).length > 0) {
        acceptedIds.forEach((id) => dispatch(removeFromCart(id)));
        setDeclineErrors(newDeclineErrors);
        const remaining = activeCartItems.filter((i) => !acceptedIds.has(i.id));
        apiSaveCart({ unitToken, cart: remaining }).catch(() => {});
        return;
      }

      trackLottoEvent(
        'bets_registered',
        buildRegisteredBetsPayload(addedBets, activeCartItems, {
          totalStake,
          totalPotentialPayout,
        })
      );

      setSuccess(true);
      apiDeleteCart({ unitToken }).catch(() => {});
      dispatch(clearCart());

      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, LOTTO_CONSTANTS.CART_SUCCESS_DISMISS_DELAY);
    } catch (err) {
      setError(getLottoErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="bottom"
      customClass={classes.DrawerPaper}
    >
      <div className={classes.Puller} />

      <div className={classes.Header}>
        <div className={classes.HeaderTitleGroup}>
          <h3 className={classes.HeaderTitle}>Cart</h3>
          {cartItems.length > 0 && (
            <span className={classes.Badge}>{totalBetsCount}</span>
          )}
        </div>

        {cartItems.length > 0 && (
          <button type="button" onClick={handleClearAll} className={classes.ClearButton}>
            <span className={classes.TrashIcon} aria-hidden="true" />
            Clear All
          </button>
        )}

        <CloseButton onClick={onClose} />
      </div>

      <div className={classes.Content}>
        {cartItems.length === 0 ? (
          <div className={classes.Empty}>
            {success ? (
              <p className={classes.SuccessText}>
                <img src={infoIcon} alt="" />
                All bets placed successfully!
              </p>
            ) : (
              <p className={classes.EmptyText}>Your cart is empty</p>
            )}
          </div>
        ) : (
          cartItems.map((item) => (
            <CartItem
              key={item.id}
              item={item}
              totalBalance={totalBalance}
              onRemove={handleRemoveItem}
              declineError={declineErrors[item.id]}
            />
          ))
        )}
      </div>

      {cartItems.length > 0 && (
        <div className={classes.Footer}>
          <div className={classes.TotalRow}>
            <div className={classes.TotalField}>
              <span className={classes.Label}>TOTAL PAY</span>
              <span className={classes.TotalValue}>{formatBalance(totalStake, 'ZAR')}</span>
            </div>
            <div className={classes.TotalField}>
              <span className={classes.Label}>POTENTIAL PAYOUT</span>
              <span className={classes.TotalPayoutValue}>
                {formatBalance(totalPotentialPayout, 'ZAR')}
              </span>
            </div>
          </div>

          {!isUserLogin ? (
            <p className={classes.HintText}>Please log in to place your bet</p>
          ) : (
            exceedsBalance && (
              <p className={classes.HintText}>Total pay exceeds your balance</p>
            )
          )}

          <button
            type="button"
            onClick={handlePlaceBets}
            disabled={isLoading || !canPlaceBets}
            className={classes.PlaceBetsButton}
          >
            {isLoading
              ? 'Processing…'
              : `Place ${totalBetsCount} Bet${totalBetsCount > 1 ? 's' : ''} ${formatBalance(totalStake, 'ZAR')}`}
          </button>

          {error && <p className={classes.ErrorText}>{error}</p>}
        </div>
      )}
    </Drawer>
  );
};

export default CartDrawer;
